import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, LogOut, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const HARDCODED_FALLBACK = [
  { id: "basico", label: "Básico", price: 49, limits: { label: "2 operadores · 5 guichês · slides + RSS" } },
  { id: "pro", label: "Pro", price: 149, limits: { label: "10 operadores · 15 guichês · vídeos + RSS" } },
  { id: "enterprise", label: "Enterprise", price: 299, limits: { label: "Ilimitado · playlist mista · suporte prioritário" } },
];

const fmtBRL = (n) =>
  Number(n).toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 0 });

const dashboardForRole = (role) => {
  if (role === "super_admin") return "/super-admin";
  if (role === "owner") return "/admin";
  if (role === "operator") return "/operator";
  return "/";
};

const Landing = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [plans, setPlans] = useState(HARDCODED_FALLBACK);
  const [trialDays, setTrialDays] = useState(14);
  const [billingCycle, setBillingCycle] = useState("annual"); // default annual = highlight discount
  const [annualDiscountPct, setAnnualDiscountPct] = useState(30);
  const [openForm, setOpenForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    plan_interest: "nao_sei",
    message: "",
  });

  // Auto-redirect logged-in users to their dashboard
  useEffect(() => {
    if (user) {
      navigate(dashboardForRole(user.role), { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    let alive = true;
    api
      .get("/billing/plans")
      .then(({ data }) => {
        if (!alive) return;
        if (Array.isArray(data?.plans) && data.plans.length) setPlans(data.plans);
        if (data?.trial_days) setTrialDays(data.trial_days);
        if (typeof data?.annual_discount_pct === "number") setAnnualDiscountPct(data.annual_discount_pct);
      })
      .catch(() => {
        /* keep fallback */
      });
    return () => {
      alive = false;
    };
  }, []);

  const openWithPlan = (planId) => {
    setForm((p) => ({ ...p, plan_interest: planId }));
    setSubmitted(false);
    setOpenForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        company: form.company.trim() || undefined,
        plan_interest: form.plan_interest === "nao_sei" ? "nao_sei" : form.plan_interest,
        message: form.message.trim() || undefined,
        source: "landing",
      });
      setSubmitted(true);
      toast.success("Recebemos sua solicitação! Entraremos em contato em breve.");
      setForm({ name: "", email: "", phone: "", company: "", plan_interest: "nao_sei", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-foreground" />
            <span className="font-heading font-black tracking-tighter text-xl">FILA INTELIGENTE</span>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <span className="hidden md:inline text-xs uppercase tracking-[0.25em] text-foreground/60">
                  {user.name}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-none"
                  onClick={() => navigate(dashboardForRole(user.role))}
                  data-testid="landing-dashboard-btn"
                >
                  Painel
                </Button>
                <Button size="sm" variant="ghost" className="rounded-none" onClick={logout}>
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Link to="/demo" className="hidden sm:inline-block">
                  <Button size="sm" variant="ghost" className="rounded-none" data-testid="landing-demo-btn">
                    Ver Demo
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="sm" variant="outline" className="rounded-none" data-testid="landing-login-btn">
                    Entrar
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button size="sm" className="rounded-none" data-testid="landing-signup-header-btn">
                    Criar conta grátis
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-8">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-8 border border-border px-3 py-1.5">
                <Sparkles className="w-3 h-3" /> SaaS Multi-empresa · Trial {trialDays} dias grátis
              </div>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tighter leading-[0.88]">
                Chamada de senhas
                <br />
                com a sua
                <span className="text-foreground/30"> identidade visual.</span>
              </h1>
            </div>
            <div className="col-span-12 md:col-span-4 md:pt-10">
              <p className="text-base md:text-lg text-foreground/70 leading-relaxed mb-8">
                Display público, painel administrativo customizável e console do operador
                sincronizados em tempo real entre dispositivos. Dados isolados por empresa
                em backend dedicado.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/signup" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    className="rounded-none h-14 px-8 text-sm uppercase tracking-[0.2em] w-full sm:w-auto"
                    data-testid="landing-cta-hero-btn"
                  >
                    Criar conta grátis <ArrowUpRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/demo" className="w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-none h-14 px-8 text-sm uppercase tracking-[0.2em] w-full sm:w-auto"
                    data-testid="landing-cta-hero-demo-btn"
                  >
                    Ver Demo interativo
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features strip */}
      <section className="border-y border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          {[
            ["TV Display", "Sincroniza em tempo real entre vários telões"],
            ["Operador", "Console limpo: chamar, rechamar, pular, digitar manualmente"],
            ["Customização", "Cores, layout, mídia, ticker e nomenclatura"],
            ["Multi-empresa", "Isolamento total · usuários por empresa · SaaS pronto"],
          ].map(([title, sub]) => (
            <div key={title}>
              <div className="font-heading font-black tracking-tighter text-2xl">{title}</div>
              <div className="text-xs text-foreground/60 mt-2 leading-relaxed">{sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Plans */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24" data-testid="landing-plans">
        <div className="grid grid-cols-12 gap-6 mb-12">
          <div className="col-span-12 md:col-span-6">
            <div className="text-xs uppercase tracking-[0.3em] text-foreground/60 mb-4">Planos</div>
            <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter leading-[0.95]">
              Pague pelo tamanho da operação.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:pt-2">
            <p className="text-foreground/70 text-base leading-relaxed">
              Todos os planos incluem TV display, console do operador, customização visual completa
              e suporte por email. Você começa grátis por {trialDays} dias.
            </p>
          </div>
        </div>

        {/* Billing cycle toggle */}
        <div className="flex items-center gap-3 mb-8" data-testid="landing-cycle-toggle">
          <div className="inline-flex border border-border rounded-none">
            <button
              type="button"
              onClick={() => setBillingCycle("monthly")}
              className={`px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold transition-all ${
                billingCycle === "monthly" ? "bg-foreground text-background" : "bg-background text-foreground hover:bg-foreground/5"
              }`}
              data-testid="landing-cycle-monthly-btn"
            >
              Mensal
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle("annual")}
              className={`px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold transition-all border-l border-border ${
                billingCycle === "annual" ? "bg-foreground text-background" : "bg-background text-foreground hover:bg-foreground/5"
              }`}
              data-testid="landing-cycle-annual-btn"
            >
              Anual
            </button>
          </div>
          <span className="text-xs uppercase tracking-[0.2em] px-3 py-1 bg-emerald-500 text-white font-bold" data-testid="landing-discount-badge">
            Anual · {annualDiscountPct}% off
          </span>
        </div>

        <div className={`grid grid-cols-1 gap-0 border-t border-l border-border ${plans.length > 1 ? "md:grid-cols-3" : "md:grid-cols-1"}`}>
          {plans.map((p) => {
            const featured = true;
            const monthly = p.cycles?.monthly?.amount ?? p.price;
            const annualTotal = p.cycles?.annual?.amount ?? monthly * 12 * 0.7;
            const annualEffective = p.cycles?.annual?.effective_monthly ?? annualTotal / 12;
            const savings = p.cycles?.annual?.savings ?? monthly * 12 - annualTotal;
            const showAnnual = billingCycle === "annual";
            const displayPricePerMonth = showAnnual ? annualEffective : monthly;
            return (
              <div
                key={p.id}
                className={`relative border-r border-b border-border p-8 md:p-12 flex flex-col gap-6 ${
                  featured ? "bg-foreground text-background" : "bg-background"
                } ${plans.length === 1 ? "md:flex-row md:items-center md:justify-between md:gap-16" : ""}`}
                data-testid={`landing-plan-${p.id}`}
              >
                <div className="flex-1">
                  <div className={`text-xs uppercase tracking-[0.3em] mb-3 ${featured ? "text-background/70" : "text-foreground/60"}`}>
                    Plano único · {showAnnual ? "Anual" : "Mensal"}
                  </div>
                  <div className="font-heading text-3xl md:text-4xl font-black tracking-tighter leading-tight">{p.label}</div>
                  <div className="flex items-baseline gap-2 mt-6">
                    <span className="font-heading text-5xl md:text-6xl font-black tracking-tighter" data-testid="landing-price-display">
                      {fmtBRL(displayPricePerMonth)}
                    </span>
                    <span className={`text-xs uppercase tracking-[0.2em] ${featured ? "text-background/60" : "text-foreground/50"}`}>
                      / mês
                    </span>
                  </div>
                  {showAnnual && (
                    <div className={`text-sm mt-2 ${featured ? "text-background/70" : "text-foreground/60"}`} data-testid="landing-price-annual-detail">
                      Cobrado {fmtBRL(annualTotal)}/ano · Você economiza <strong className={featured ? "text-emerald-300" : "text-emerald-600"}>{fmtBRL(savings)}</strong>
                    </div>
                  )}
                  {!showAnnual && (
                    <div className={`text-sm mt-2 ${featured ? "text-background/70" : "text-foreground/60"}`} data-testid="landing-price-monthly-detail">
                      Cancele quando quiser · Sem multa
                    </div>
                  )}
                  <ul className={`space-y-2 text-sm mt-6 ${featured ? "text-background/90" : "text-foreground/80"}`}>
                    {(p.limits?.label || "").split("·").map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{item.trim()}</span>
                      </li>
                    ))}
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>Trial de {trialDays} dias sem cartão de crédito</span>
                    </li>
                  </ul>
                </div>
                <div className="flex flex-col gap-3 md:w-64 shrink-0">
                  <Link to={`/signup?interval=${billingCycle}`}>
                    <Button
                      variant={featured ? "secondary" : "default"}
                      className="rounded-none h-12 text-sm uppercase tracking-[0.2em] w-full"
                      data-testid={`landing-plan-${p.id}-btn`}
                    >
                      Criar conta grátis
                    </Button>
                  </Link>
                  <Link to="/demo">
                    <Button
                      variant="outline"
                      className={`rounded-none h-12 text-sm uppercase tracking-[0.2em] w-full ${featured ? "border-background/50 text-background hover:bg-background hover:text-foreground" : ""}`}
                      data-testid={`landing-plan-${p.id}-demo-btn`}
                    >
                      Ver demo
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-foreground/50 mt-6">
          Ao criar sua conta você aceita nossos{" "}
          <Link to="/termos-de-uso" className="underline hover:text-foreground">Termos de Uso</Link>{" "}
          e política de tratamento de dados (LGPD).
        </p>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-border bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-8">
            <h3 className="font-heading text-3xl md:text-5xl font-black tracking-tighter leading-[0.95]">
              Tem dúvida sobre qual plano serve?
              <br />
              <span className="text-background/50">Fale com a gente.</span>
            </h3>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link to="/signup" className="w-full md:w-auto">
              <Button
                size="lg"
                variant="secondary"
                className="rounded-none h-14 px-8 text-sm uppercase tracking-[0.2em] w-full md:w-auto"
                data-testid="landing-cta-bottom-btn"
              >
                Criar conta grátis <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/demo" className="w-full md:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="rounded-none h-14 px-8 text-sm uppercase tracking-[0.2em] w-full md:w-auto"
                data-testid="landing-demo-bottom-btn"
              >
                Ver Demo interativo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-wrap items-center justify-between gap-3 text-xs text-foreground/50">
          <div>© Fila Inteligente · Plataforma SaaS de Gerenciamento de Filas</div>
          <div className="flex items-center gap-4">
            <Link to="/demo" className="hover:text-foreground" data-testid="footer-demo-link">
              Demo
            </Link>
            <Link to="/termos-de-uso" className="hover:text-foreground" data-testid="footer-terms-link">
              Termos de Uso
            </Link>
            <Link to="/login" className="hover:text-foreground">
              Já é cliente? Entrar →
            </Link>
          </div>
        </div>
      </footer>

      {/* Lead form modal */}
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogTrigger asChild>
          <span style={{ display: "none" }} />
        </DialogTrigger>
        <DialogContent className="rounded-none max-w-lg" data-testid="landing-lead-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-3xl font-black tracking-tighter">
              {submitted ? "Solicitação enviada" : "Solicitar acesso"}
            </DialogTitle>
            <DialogDescription className="text-foreground/60">
              {submitted
                ? "Obrigado! Em breve entraremos em contato com as instruções de acesso."
                : "Preencha seus dados e nossa equipe vai entrar em contato para liberar seu acesso."}
            </DialogDescription>
          </DialogHeader>

          {submitted ? (
            <div className="py-6">
              <Button
                className="w-full rounded-none h-12 uppercase tracking-[0.2em]"
                onClick={() => setOpenForm(false)}
                data-testid="landing-lead-close-btn"
              >
                Fechar
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4 py-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em]">Nome*</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="mt-2 h-11 rounded-none"
                  data-testid="landing-lead-name"
                  required
                  minLength={2}
                  maxLength={120}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em]">Email*</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    className="mt-2 h-11 rounded-none"
                    data-testid="landing-lead-email"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em]">Telefone</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                    className="mt-2 h-11 rounded-none"
                    placeholder="(11) 99999-9999"
                    data-testid="landing-lead-phone"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em]">Empresa</Label>
                <Input
                  value={form.company}
                  onChange={(e) => setForm((p) => ({ ...p, company: e.target.value }))}
                  className="mt-2 h-11 rounded-none"
                  placeholder="Nome da sua empresa / clínica"
                  data-testid="landing-lead-company"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em]">Plano de interesse</Label>
                <Select
                  value={form.plan_interest}
                  onValueChange={(v) => setForm((p) => ({ ...p, plan_interest: v }))}
                >
                  <SelectTrigger className="mt-2 h-11 rounded-none" data-testid="landing-lead-plan-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basico">Básico</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                    <SelectItem value="nao_sei">Ainda não sei</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em]">Mensagem</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                  className="mt-2 rounded-none min-h-[88px]"
                  placeholder="Conte um pouco sobre seu fluxo (qtd de operadores, guichês, etc.)"
                  data-testid="landing-lead-message"
                  maxLength={2000}
                />
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="rounded-none uppercase tracking-[0.2em] w-full h-12"
                  data-testid="landing-lead-submit"
                >
                  {submitting ? "Enviando…" : "Enviar solicitação"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Landing;
